import { pipe } from 'fp-ts/function';
import { record } from 'fp-ts';
import { useState } from 'react';
import { keyBy } from '@mews/fp';
import { lt, showSuccessAlert, useFeatureFlag } from '@mews/framework';
import { ErrorHandler, LargeContentContainer, OverlayedLoader } from '@mews-commander/ui';
import { CheckInConfiguration, CheckInConfigurationType } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { localizeText, useCommanderThunkDispatch } from '@mews-commander/utils';
import { updateCheckInConfigurations, useCheckInConfigurations } from '../../apiHooks';
import { reportCheckInConfigurationError } from '../../actions';
import { CheckInFieldsTableContainer } from '../table/CheckInFieldsTableContainer';
import {
    CheckInCheckboxStates,
    CheckInFieldsTooltipValues,
    CheckInFieldsValues,
    ConfigurationEditableColumnValues,
} from '../types';
import { getConfigurationTooltip } from '../tooltip/mapTooltipText';
import { evaluateFormula } from '../formulaLabel/evaluateFormula';
import { convertTableValuesToUpdates } from './utils';

interface CheckInFieldsContainerProps {
    enterpriseId: string;
}

export const CheckInFieldsContainer = ({ enterpriseId }: CheckInFieldsContainerProps) => {
    const dispatch = useCommanderThunkDispatch();
    const carRegistrationNumberCheckInFormEnabled = useFeatureFlag(FeatureFlag.carRegistrationNumberCheckInFormEnabled);
    const guestProfileDietaryRequirementsEnabled = useFeatureFlag(FeatureFlag.guestProfileDietaryRequirementsEnabled);
    const areExtendedHotelConfiguredFieldsEnabled = useFeatureFlag(FeatureFlag.extendedHotelConfiguredFields);

    const { data: checkInConfigurations, hasFailed } = useCheckInConfigurations({ enterpriseId });
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (checkInConfigurations == null) {
        if (hasFailed) {
            return <ErrorHandler />;
        }
        return <OverlayedLoader show />;
    }

    const { configurations } = checkInConfigurations;

    const updatedConfigurations = configurations.filter((configuration) => {
        if (configuration.type === CheckInConfigurationType.CarRegistrationNumber) {
            return carRegistrationNumberCheckInFormEnabled;
        }
        if (configuration.type === CheckInConfigurationType.DietaryRequirements) {
            return guestProfileDietaryRequirementsEnabled;
        }
        return true;
    });

    const configurationRecord = keyBy('type', updatedConfigurations) as Record<
        CheckInConfigurationType,
        CheckInConfiguration
    >;

    const tooltipValues: CheckInFieldsTooltipValues = pipe(
        configurationRecord,
        record.map(
            ({ reservationOwnerFormula: ownerFormula, reservationAdultFormula, reservationChildFormula, type }) => {
                // agreed fallback chain
                const adultFormula = reservationAdultFormula ?? ownerFormula;
                const childFormula = reservationChildFormula ?? adultFormula;

                return {
                    reservationOwner: getConfigurationTooltip({
                        formula: ownerFormula ?? undefined,
                        type,
                        localizeText,
                    }),
                    reservationAdult: getConfigurationTooltip({
                        formula: adultFormula ?? undefined,
                        type,
                        localizeText,
                    }),
                    reservationChild: getConfigurationTooltip({
                        formula: childFormula ?? undefined,
                        type,
                        localizeText,
                    }),
                };
            }
        )
    );

    const configurationRecordValues: CheckInFieldsValues = pipe(
        configurationRecord,
        record.map((configuration) => ({
            reservationOwner: configuration.reservationOwnerValue,
            reservationAdult: configuration.reservationAdultValue,
            reservationChild: configuration.reservationChildValue,
        }))
    );

    const isLegallyRequiredRecord = pipe(
        configurationRecord,
        record.map(({ reservationOwnerFormula }) =>
            reservationOwnerFormula != null ? evaluateFormula(reservationOwnerFormula) : false
        )
    );

    const configurationEditableValues: ConfigurationEditableColumnValues = pipe(
        configurationRecord,
        record.map((configuration) => ({
            reservationOwner: configuration.reservationOwnerIsEditable ?? true,
            reservationAdult: configuration.reservationAdultIsEditable ?? true,
            reservationChild: configuration.reservationChildIsEditable ?? true,
        }))
    );

    const handleSubmit = async (visibilityValues: CheckInFieldsValues, checkboxStates: CheckInCheckboxStates) => {
        const updates = convertTableValuesToUpdates({
            tableValues: visibilityValues,
            configurations,
            areExtendedHotelConfiguredFieldsEnabled,
            checkboxStates,
        });

        setIsSubmitting(true);
        try {
            await dispatch(updateCheckInConfigurations({ enterpriseId, updates }));
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        } catch (error) {
            dispatch(
                reportCheckInConfigurationError({
                    error,
                    extra: { enterpriseId },
                })
            );
            setIsSubmitting(false);
        }
        setIsSubmitting(false);
    };

    return (
        <>
            <OverlayedLoader show={isSubmitting} />
            <LargeContentContainer>
                <CheckInFieldsTableContainer
                    isLegallyRequiredRecord={isLegallyRequiredRecord}
                    editableValues={configurationEditableValues}
                    initialValues={configurationRecordValues}
                    tooltipValues={tooltipValues}
                    onSubmit={handleSubmit}
                />
            </LargeContentContainer>
        </>
    );
};
